import React, { useContext, useEffect, useRef } from "react";
import classnames from "classnames";

import Thumbnail from "../../image-stream/thumbnail";

import { TaskContext } from "contexts/task";
import { AttributesContext } from "contexts/attributes";
import { ProductHeader } from "./product-header";
import { CoderRolesHeader } from "./coder-roles-header";
import { useIsSticky } from "shared/hooks";
import { useSidePanel } from "shared/hooks/use-side-panel";
import { useColumnSize } from "shared/hooks/use-column-size";
import { useColumnTemplate } from "shared/hooks/use-column-template";
import { attributeUUID } from "shared/attribute-uuid";
import { TaskType } from "shared/taskTypeMap";

import moment from "moment";
import { MOMENT_LONG_DATE_FORMAT } from "shared/constants";

const CompareHeader = ({
    referenceProducts,
    toggleModalSelect,
    hideSelect,
    onColumnWidthChange,
    columnWidths,
    onInitColSizes,
    isQa,
    roleLabels,
    hasRedo,
    setHasRedo,
    refetchCoderRoles,
    coderTypeLabel,
    isBrandVerified,
    styleOverride,
}) => {
    const { gridTemplateColumns } = useColumnTemplate(
        roleLabels ? roleLabels.length + 1 + referenceProducts.length : referenceProducts.length + 2,
        columnWidths,
        !roleLabels && referenceProducts.length === 0
    );
    const refCompareWrapper = useRef();
    const isSticky = useIsSticky(refCompareWrapper);
    const { task } = useContext(TaskContext);
    const { data: attributesData } = useContext(AttributesContext);
    const { colRef: placeholderColRef, initialize: placeholderInit, width: placeholderWidth, resizerRef: placeholderResizerRef } = useColumnSize();
    const { isSalesSample } = useSidePanel();

    // TODO: Move BVQA Compare header into its own separate component
    const isBVQA = task.sourcePage === "brandverifiedqa";
    const updatedBy = !isBVQA && attributesData[attributeUUID.updatedBy];
    const updatedOn = !isBVQA && attributesData[attributeUUID.updatedOn];

    const isResizerEnabled = task.tags.includes("FinalQA") || isQa;

    useEffect(() => {
        onInitColSizes();
    }, [referenceProducts.length]);

    useEffect(() => {
        if (placeholderWidth !== 0) {
            // always 1 because it's reference selector col (2nd col)
            onColumnWidthChange(1, placeholderWidth);
        }
    }, [onColumnWidthChange, placeholderWidth]);

    const coderType = task.taskType.split(".").slice(-2)[0];
    const coderRole = task.taskType.split(".").slice(-1)[0];
    const coderLabel = `${isBrandVerified ? "Brand" : ""} ${coderType} Coder ${coderRole}`;

    return (
        <>
            <div ref={refCompareWrapper} />
            <div className={classnames("product-compare", { "-sticky": roleLabels ? false : isSticky }, { "-initial": !!roleLabels })}>
                <div
                    className="product-compare-header"
                    style={{ gridTemplateColumns }}
                >
                    <div>
                        <Thumbnail
                            photoKey={task.context.photographyWipKey}
                            shipmentKey={task.shipmentKey}
                            shipmentProductKey={task.shipmentProductKey}
                            size="small"
                            coderTypeLabel={coderTypeLabel}
                            disablePopout={task.taskType !== TaskType.FINAL_QA}
                        />
                    </div>

                    {roleLabels &&
                        roleLabels.map((role, index) => {
                            return (
                                <CoderRolesHeader
                                    key={index}
                                    role={role}
                                    col={index + 1}
                                    onColumnWidthChange={onColumnWidthChange}
                                    toggleModalSelect={toggleModalSelect}
                                    hideSelect={hideSelect}
                                    isCompleted={task.isCompleted}
                                    task={task}
                                    hasRedo={hasRedo}
                                    setHasRedo={setHasRedo}
                                    refetchCoderRoles={refetchCoderRoles}
                                    isSalesSample={isSalesSample}
                                    isBrandVerified={isBrandVerified}
                                    styleOverride={styleOverride}
                                />
                            );
                        })}

                    {task.tags.includes("Coding") && (
                        <div>
                            <div className="coder-role-details">
                                <h4>{coderLabel}</h4>
                                <p>Last updated: {moment.utc(updatedOn).format(MOMENT_LONG_DATE_FORMAT)}</p>
                                <p>Last updated by: {updatedBy}</p>
                            </div>

                            {!task.isCompleted && !hideSelect && (
                                <>
                                    <h5>References:</h5>
                                    <div className="button-group -stacked">
                                        <button
                                            className="button"
                                            onClick={toggleModalSelect}
                                        >
                                            Select Reference
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {task.tags.includes("FinalQA") && (
                        <div
                            ref={placeholderColRef}
                            onMouseDown={placeholderInit}
                        >
                            {
                                <div
                                    ref={placeholderResizerRef}
                                    className="border-resizer"
                                />
                            }
                            {!task.isCompleted && !hideSelect && (
                                <>
                                    <h5>References:</h5>
                                    <div className="button-group -stacked">
                                        <button
                                            className="button"
                                            onClick={toggleModalSelect}
                                        >
                                            Select Reference
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {referenceProducts.map((product, index) => {
                        const isLastCol = referenceProducts.length === index + 1;

                        return (
                            <ProductHeader
                                key={product.key}
                                product={product}
                                col={index + 2}
                                onColumnWidthChange={onColumnWidthChange}
                                lastCol={isLastCol}
                                resizerEnabled={isResizerEnabled}
                            />
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default CompareHeader;
